import { useState, useEffect } from 'react';

/**
 * useTypewriter hook
 * @param {string} text - Text to type out
 * @param {number} speed - Typing delay in ms per character (default: 38)
 * @param {number} startDelay - Initial delay before typing starts in ms (default: 600)
 * @returns {{ displayed: string, done: boolean }}
 */
export function useTypewriter(text, speed = 38, startDelay = 600) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Respect reduced motion
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setDisplayed(text);
      setDone(true);
      return;
    }

    setDisplayed('');
    setDone(false);

    let charIndex = 0;
    let typeInterval = null;

    const startTimer = setTimeout(() => {
      typeInterval = setInterval(() => {
        charIndex += 1;
        setDisplayed(text.slice(0, charIndex));

        if (charIndex >= text.length) {
          clearInterval(typeInterval);
          setDone(true);
        }
      }, speed);
    }, startDelay);

    return () => {
      clearTimeout(startTimer);
      if (typeInterval) {
        clearInterval(typeInterval);
      }
    };
  }, [text, speed, startDelay]);

  return { displayed, done };
}

export default useTypewriter;
